import { Debug } from './debug';

const CLEANUP_TIMEOUT_MS = 1_000;

export class Pool<T, P> {
    private static readonly pools = new Map<string, Pool<any, any>>();

    static getPool<T, P>(
        name: string,
        buildItem: (p: P) => T,
        releaseItem: (i: T) => void,
        destroyItem: (i: T) => void,
        maxPoolSize: number
    ) {
        if (!this.pools.has(name)) {
            this.pools.set(name, new Pool<T, P>(name, buildItem, releaseItem, destroyItem, maxPoolSize));
        }
        return this.pools.get(name) as Pool<T, P>;
    }

    static readonly debug = Debug.create(true, 'pool');

    private readonly freePool: T[] = [];
    private readonly busyPool = new Set<T>();
    private cleanPoolTimer?: NodeJS.Timeout;
    private cleanPoolDue?: number;

    public constructor(
        private readonly name: string,
        private readonly buildItem: (params: P) => T,
        private readonly releaseItem: (item: T) => void,
        private readonly destroyItem: (item: T) => void,
        private readonly maxPoolSize: number,
        private readonly cleanupTimeMs = CLEANUP_TIMEOUT_MS
    ) {}

    public isFull() {
        return this.freePool.length + this.busyPool.size >= this.maxPoolSize;
    }

    public hasFree() {
        return this.freePool.length > 0;
    }

    public obtain(params: P) {
        if (!this.hasFree() && this.isFull()) {
            throw new Error('AG Charts - pool exhausted');
        }

        let nextFree = this.freePool.pop();
        if (nextFree == null) {
            nextFree = this.buildItem(params);
            Pool.debug(() => [
                `Pool[name=${this.name}]: Created instance (${this.freePool.length} / ${this.busyPool.size + 1} / ${this.maxPoolSize})`,
                nextFree,
            ]);
        } else {
            Pool.debug(() => [
                `Pool[name=${this.name}]: Re-used instance (${this.freePool.length} / ${this.busyPool.size + 1} / ${this.maxPoolSize})`,
                nextFree,
            ]);
        }

        this.busyPool.add(nextFree);
        return { item: nextFree, release: () => this.release(nextFree) };
    }

    public obtainFree() {
        const nextFree = this.freePool.pop();
        if (nextFree == null) {
            throw new Error('AG Charts - pool has no free instances');
        }
        Pool.debug(() => [
            `Pool[name=${this.name}]: Re-used instance (${this.freePool.length} / ${this.busyPool.size + 1} / ${this.maxPoolSize})`,
            nextFree,
        ]);

        this.busyPool.add(nextFree);
        return { item: nextFree, release: () => this.release(nextFree) };
    }

    public release(item: T) {
        if (!this.busyPool.has(item)) {
            throw new Error('AG Charts - cannot free item from pool which is not tracked as busy.');
        }

        Pool.debug(() => [
            `Pool[name=${this.name}]: Releasing instance (${this.freePool.length} / ${this.busyPool.size} / ${this.maxPoolSize})`,
            item,
        ]);

        this.releaseItem(item);
        this.busyPool.delete(item);
        this.freePool.push(item);

        Pool.debug(() => [
            `Pool[name=${this.name}]: Returned instance to free pool (${this.freePool.length} / ${this.busyPool.size} / ${this.maxPoolSize})`,
            item,
        ]);

        const now = Date.now();
        const earliestClean = now + this.cleanupTimeMs * 0.5;
        if (this.cleanPoolTimer && (this.cleanPoolDue ?? Infinity) < earliestClean) {
            // Avoid clearing the timeout repeatedly (e.g. when many items are returned successively)
            // as this can be an expensive operation based upon Chrome profiling.
            clearTimeout(this.cleanPoolTimer);
            this.cleanPoolTimer = undefined;
        }
        if (!this.cleanPoolTimer) {
            this.cleanPoolDue = now + this.cleanupTimeMs;
            this.cleanPoolTimer = setTimeout(this.cleanPool.bind(this), this.cleanupTimeMs);
        }
    }

    private cleanPool() {
        const itemsToFree = this.freePool.splice(0);
        for (const item of itemsToFree) {
            this.destroyItem(item);
        }

        Pool.debug(() => [
            `Pool[name=${this.name}]: Cleaned pool of ${itemsToFree.length} items (${this.freePool.length} / ${this.busyPool.size} / ${this.maxPoolSize})`,
        ]);
    }

    destroy() {
        this.cleanPool();
        for (const item of this.busyPool.values()) {
            this.destroyItem(item);
        }
        this.busyPool.clear();
    }
}
